import { QueryClient, defaultShouldDehydrateQuery } from '@tanstack/react-query';
import type { Query } from '@tanstack/react-query';
import type { PersistedClient, Persister } from '@tanstack/react-query-persist-client';
import { del, get, set } from 'idb-keyval';

import { sanitizeQueryData } from '../utils/reactQuerySanitizer';

import { logger } from '@/utils/logger';

/**
 * Centralized React Query configuration for optimal caching and performance
 *
 * Key optimizations:
 * 1. Intelligent stale times based on data type
 * 2. Reduced refetching on window focus
 * 3. Longer garbage collection times
 * 4. Smart retry logic
 */

// Cache time configurations (in milliseconds)
export const CACHE_TIMES = {
  // Platform connection status - changes infrequently
  CONNECTION_STATUS: 15 * 60 * 1000, // 15 minutes

  // Analytics data - balance between freshness and performance
  ANALYTICS: 5 * 60 * 1000, // 5 minutes

  // Social metrics - can be slightly stale
  SOCIAL_METRICS: 10 * 60 * 1000, // 10 minutes

  // AI insights - expensive to generate
  AI_INSIGHTS: 15 * 60 * 1000, // 15 minutes

  // User profile/settings - rarely changes
  USER_DATA: 30 * 60 * 1000, // 30 minutes

  // Feed/content data - frequently updated
  FEED_DATA: 2 * 60 * 1000, // 2 minutes

  // Product data - changes occasionally
  PRODUCT_DATA: 10 * 60 * 1000, // 10 minutes
};

// Garbage collection times (how long to keep unused data in cache)
export const GC_TIMES = {
  DEFAULT: 30 * 60 * 1000, // 30 minutes
  LONG: 60 * 60 * 1000, // 1 hour
  SESSION: 24 * 60 * 60 * 1000, // 24 hours
};

/**
 * Get cache configuration based on query key
 */
export function getCacheConfig(queryKey: unknown[]) {
  const key = queryKey[0] as string;

  // Connection status queries
  if (key.includes('connection') || key.includes('integration')) {
    return {
      staleTime: CACHE_TIMES.CONNECTION_STATUS,
      gcTime: GC_TIMES.LONG,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    };
  }

  // Analytics queries
  if (key.includes('analytics') || key.includes('metrics')) {
    return {
      staleTime: CACHE_TIMES.ANALYTICS,
      gcTime: GC_TIMES.DEFAULT,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    };
  }

  // AI/ML queries
  if (key.includes('ai') || key.includes('insight') || key.includes('recommendation')) {
    return {
      staleTime: CACHE_TIMES.AI_INSIGHTS,
      gcTime: GC_TIMES.LONG,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    };
  }

  // User data queries
  if (key.includes('user') || key.includes('profile') || key.includes('settings')) {
    return {
      staleTime: CACHE_TIMES.USER_DATA,
      gcTime: GC_TIMES.SESSION,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    };
  }

  // Feed/content queries
  if (key.includes('feed') || key.includes('post') || key.includes('content')) {
    return {
      staleTime: CACHE_TIMES.FEED_DATA,
      gcTime: GC_TIMES.DEFAULT,
      refetchOnWindowFocus: true, // Keep feed fresh
      refetchOnReconnect: true,
    };
  }

  // Product queries
  if (key.includes('product') || key.includes('shopify')) {
    return {
      staleTime: CACHE_TIMES.PRODUCT_DATA,
      gcTime: GC_TIMES.DEFAULT,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    };
  }

  // Default configuration for other queries
  return {
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: GC_TIMES.DEFAULT,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  };
}

/**
 * Create an optimized QueryClient with intelligent defaults
 */
export function createOptimizedQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Dynamic cache configuration based on query type
        staleTime: 5 * 60 * 1000, // Default: 5 minutes
        gcTime: GC_TIMES.DEFAULT, // Default: 30 minutes

        // Reduce unnecessary refetches
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        refetchOnMount: true,

        // Smart retry logic
        retry: (failureCount, error: any) => {
          // Don't retry on 404s or auth errors
          if (error?.status === 404 || error?.status === 401 || error?.status === 403) {
            return false;
          }

          // Don't retry on authentication errors
          if (
            error?.message?.includes('User not authenticated') ||
            error?.message?.includes('unauthorized') ||
            error?.message?.includes('Unauthorized')
          ) {
            return false;
          }

          // Don't retry if React hooks are corrupted
          if (
            error?.message?.includes('useEffect') ||
            error?.message?.includes('Invalid hook call')
          ) {
            return false;
          }

          // Retry Supabase auth-js navigator lock "steal" races aggressively.
          // These are transient cross-tab / concurrent-request conflicts that
          // resolve on retry.
          if (
            error?.message?.includes("'steal' option") ||
            error?.message?.includes('Lock broken by another request') ||
            error?.message?.includes('was released because another request stole it') ||
            error?.message?.includes('Lock "lock:sb-')
          ) {
            return failureCount < 3;
          }

          // Retry up to 3 times with exponential backoff
          return failureCount < 3;
        },

        // Retry delay with exponential backoff
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

        // Network mode
        networkMode: 'offlineFirst', // Use cache when possible

        // Global onSuccess handler to sanitize data before Sentry captures it
        onSuccess: (data) => {
          // Sanitize data to prevent undefined date serialization issues in Sentry
          try {
            return sanitizeQueryData(data);
          } catch (error) {
            logger.warn('Failed to sanitize query data in global handler:', error);
            return data;
          }
        },
      },

      mutations: {
        // Optimistic updates
        networkMode: 'offlineFirst',

        // Retry failed mutations
        retry: 1,
        retryDelay: 1000,
      },
    },
  });
}

/**
 * Query key factory for consistent key generation
 */
export const queryKeys = {
  // Workflow template queries return raw row arrays. The version segment is
  // intentional: before the Projects rail, this cache stored a ServiceResponse
  // envelope under the legacy key. Persisted IndexedDB entries live for a day.
  workflows: {
    templates: () => ['workflow-templates', 'catalog', 2] as const,
    hidden: () => ['workflow-templates', 'hidden', 2] as const,
    inputKind: (inputKind: 'article' | 'photos' | 'url' | 'manual') =>
      ['workflow-templates', 'input-kind', 2, inputKind] as const,
  },

  // Platform integrations
  integrations: {
    all: ['integrations'] as const,
    connections: (userId: string) => ['integrations', 'connections', userId] as const,
    platform: (platform: string, userId: string) => ['integrations', platform, userId] as const,
  },

  // Analytics
  analytics: {
    all: ['analytics'] as const,
    dashboard: (userId: string, dateRange: { start: string; end: string }) =>
      ['analytics', 'dashboard', userId, dateRange] as const,
    platform: (platform: string, userId: string, dateRange: { start: string; end: string }) =>
      ['analytics', platform, userId, dateRange] as const,
    accountLevel: (userId: string, dateRange: { from: Date; to: Date }, accountIds: string[]) =>
      ['analytics', 'accountLevel', userId, dateRange, accountIds] as const,
  },

  // AI insights
  ai: {
    all: ['ai'] as const,
    insights: (userId: string) => ['ai', 'insights', userId] as const,
    recommendations: (userId: string, context: string) =>
      ['ai', 'recommendations', userId, context] as const,
  },

  // Social metrics
  social: {
    all: ['social'] as const,
    metrics: (userId: string, platforms: string[]) =>
      ['social', 'metrics', userId, ...platforms] as const,
    followers: (userId: string) => ['social', 'followers', userId] as const,
  },

  // Posts & Content Feed
  posts: {
    all: ['posts'] as const,
    lists: () => ['posts', 'list'] as const,
    list: (userId?: string, filters?: unknown) => ['posts', 'list', userId, filters] as const,
    details: () => ['posts', 'detail'] as const,
    detail: (postId: string) => ['posts', 'detail', postId] as const,
    counts: () => ['posts', 'count'] as const,
    count: (userId?: string, filters?: unknown) => ['posts', 'count', userId, filters] as const,
  },

  // Brand Kits & Multi-Tenant Workspaces
  brandKits: {
    all: ['brand-kits'] as const,
    list: (ownerId?: string) => ['brand-kits', ownerId] as const,
    activePointer: (ownerId?: string) => ['active-workspace-pointer', ownerId] as const,
    effectiveWorkspace: (userId?: string) => ['effective-workspace-id', userId] as const,
    companyName: (ownerId?: string) => ['user-profile-company-name', ownerId] as const,
  },

  // Brand Audiences
  audiences: {
    all: ['brand-audiences'] as const,
    list: (ownerId?: string) => ['brand-audiences', ownerId] as const,
  },

  // Campaigns & Projects
  campaigns: {
    all: ['campaigns'] as const,
    list: (userId?: string) => ['campaigns', userId] as const,
    detail: (campaignId: string) => ['campaigns', 'detail', campaignId] as const,
  },

  // Mora Reach — Events (Phase 1)
  reach: {
    all: ['reach'] as const,
    events: {
      all: ['reach', 'events'] as const,
      list: (workspaceId?: string | null) => ['reach', 'events', 'list', workspaceId] as const,
      detail: (workspaceId?: string | null, eventId?: string) =>
        ['reach', 'events', 'detail', workspaceId, eventId] as const,
      /** The active `project_sources`-bound plan for this event, if any. */
      plan: (workspaceId?: string | null, eventId?: string) =>
        ['reach', 'events', 'plan', workspaceId, eventId] as const,
    },
    ingestBatches: {
      all: ['reach', 'ingest-batches'] as const,
      list: (workspaceId?: string | null, eventId?: string | null) =>
        ['reach', 'ingest-batches', 'list', workspaceId, eventId] as const,
    },
    participants: {
      all: ['reach', 'participants'] as const,
      list: (workspaceId?: string | null, eventId?: string | null) =>
        ['reach', 'participants', 'list', workspaceId, eventId] as const,
    },
    // Phase 3 — scoring pipeline. `people` is the joined
    // participant+person+company+latest-assessment+relationship view the
    // Event People tab reads; `assessments` is the raw versioned-assessment
    // list a single row's score breakdown reads from.
    people: {
      all: ['reach', 'people'] as const,
      list: (workspaceId?: string | null, eventId?: string | null) =>
        ['reach', 'people', 'list', workspaceId, eventId] as const,
    },
    assessments: {
      all: ['reach', 'assessments'] as const,
      forEvent: (workspaceId?: string | null, eventId?: string | null) =>
        ['reach', 'assessments', 'event', workspaceId, eventId] as const,
      forParticipant: (workspaceId?: string | null, participantId?: string | null) =>
        ['reach', 'assessments', 'participant', workspaceId, participantId] as const,
    },
    // Phase 4 — Mark's printable event briefing. Composes participants + latest
    // assessment + interactions + outreach touches for one event; see
    // src/lib/reach/briefing/fetch-event-brief-data.ts.
    brief: {
      all: ['reach', 'brief'] as const,
      forEvent: (workspaceId?: string | null, eventId?: string | null) =>
        ['reach', 'brief', 'event', workspaceId, eventId] as const,
    },
    // Phase 6/7 — the global People pool (`/reach/people`) and generalised
    // Lists (`/reach/lists`). `personDetail`/`companyDetail` back the §16
    // right-dock panel; `pool` is the joined one-row-per-person view §9.4
    // reads from (src/services/data/reach/ReachPersonService.ts).
    pool: {
      all: ['reach', 'pool'] as const,
      list: (workspaceId?: string | null) => ['reach', 'pool', 'list', workspaceId] as const,
      page: (workspaceId?: string | null) => ['reach', 'pool', 'page', workspaceId] as const,
      personDetail: (workspaceId?: string | null, personId?: string | null) =>
        ['reach', 'pool', 'person-detail', workspaceId, personId] as const,
      companyDetail: (workspaceId?: string | null, companyId?: string | null) =>
        ['reach', 'pool', 'company-detail', workspaceId, companyId] as const,
      eventAssessment: (
        workspaceId?: string | null,
        personId?: string | null,
        eventId?: string | null,
      ) => ['reach', 'pool', 'event-assessment', workspaceId, personId, eventId] as const,
    },
    lists: {
      all: ['reach', 'lists'] as const,
      list: (workspaceId?: string | null) => ['reach', 'lists', 'list', workspaceId] as const,
      detail: (workspaceId?: string | null, listId?: string | null) =>
        ['reach', 'lists', 'detail', workspaceId, listId] as const,
    },
    // Phase 5 — outreach draft editor + LinkedIn copy-and-open (§19).
    // `forEvent`/`forList` back the queue (§19.7); `draft` backs one target's
    // editor (person + event/list + channel + sequence step).
    outreach: {
      all: ['reach', 'outreach'] as const,
      forEvent: (workspaceId?: string | null, eventId?: string | null) =>
        ['reach', 'outreach', 'event', workspaceId, eventId] as const,
      forList: (workspaceId?: string | null, listId?: string | null) =>
        ['reach', 'outreach', 'list', workspaceId, listId] as const,
      draft: (
        workspaceId?: string | null,
        personId?: string | null,
        eventId?: string | null,
        listId?: string | null,
        sequenceStep?: number | null,
      ) =>
        [
          'reach',
          'outreach',
          'draft',
          workspaceId,
          personId,
          eventId,
          listId,
          sequenceStep,
        ] as const,
    },
  },

  // Plans & Slots
  plans: {
    all: ['plans'] as const,
    slots: (userId?: string, planId?: string | null, statusKey?: string) =>
      ['planSlots', userId ?? 'anon', planId ?? null, statusKey] as const,
  },

  // Credit Wallet & Receipts
  credits: {
    all: ['credits'] as const,
    wallet: (ownerId?: string) => ['credit-wallet', ownerId] as const,
    ledger: (ownerId?: string, kind?: string) => ['credit-ledger', ownerId, kind] as const,
    receipts: (ownerId?: string) => ['credit-receipts', ownerId] as const,
  },
};

/**
 * Creates an Indexed DB persister
 * @see https://developer.mozilla.org/en-US/docs/Web/API/IndexedDB_API
 */
export function createIDBPersister(idbValidKey: IDBKeyRange | IDBValidKey = 'reactQuery') {
  return {
    persistClient: async (client: PersistedClient) => {
      await set(idbValidKey, client);
    },
    restoreClient: async () => {
      return await get<PersistedClient>(idbValidKey);
    },
    removeClient: async () => {
      await del(idbValidKey);
    },
  } satisfies Persister;
}

export const idbPersister = createIDBPersister();

/**
 * Shared persist options for both `PersistQueryClientProvider` mount sites in
 * `main.tsx`, so the two can never drift apart.
 *
 * 🔴 `shouldDehydrateQuery` — WHY ANY QUERY WOULD OPT OUT OF A 24-HOUR CACHE.
 *
 * The cache is persisted to IndexedDB with `maxAge` 24h, and on the next page
 * load a restored entry is served INSTANTLY: `isFetched` is already true and
 * the data is already there, before any request goes out. For almost
 * everything that is exactly what we want — the app paints immediately and
 * revalidates behind it.
 *
 * It is wrong for data a caller reads ONCE at construction and cannot adopt
 * later. Measured 2026-09-07: `ai-session-messages` was fetched mid-turn,
 * while the just-sent exchange had not been written yet, so an EMPTY array was
 * cached and persisted. On the next load that empty result was restored
 * instantly, the composer mounted `useChat` with it, and because
 * `initialMessages` is read once at construction the real history — which
 * arrived 300ms later, 200 OK, both messages — was never adopted. The user's
 * conversation was invisible for the life of that page load. Reload again and
 * it was all there, because by then the cache held the good copy.
 *
 * So: opt out with `meta: { persist: false }` when a query feeds a
 * construction-time input, or when a stale copy is worse than a brief blank.
 * Everything else keeps persisting exactly as before.
 */
export const persistOptions = {
  persister: idbPersister,
  maxAge: 1000 * 60 * 60 * 24,
  dehydrateOptions: {
    shouldDehydrateQuery: (query: Query) =>
      query.meta?.persist !== false && defaultShouldDehydrateQuery(query),
  },
};
